import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Button, Color, Icon, Input, Spin, Text } from '@bb/elements-web';
import { isValidEmail } from 'utils';
import {
  CanManageBillingProfileType,
  useSubscriptionsPageContext,
} from '../../SubscriptionsPageContext';
import { CardStatus } from './CardStatus';

type BillingEmailCardProps = {
  canManageBilling: CanManageBillingProfileType;
};

/**
 * The "Email" section within the Billing Contact Details card.
 * User/Brokerage can view & update their email address from here.
 */
const BillingEmailCard: React.FC<BillingEmailCardProps> = ({
  canManageBilling,
}) => {
  const {
    billingProfile,
    updateBillingProfile,
    isFetchingProfile,
    isUpdatingProfile,
    hasSavedCard,
  } = useSubscriptionsPageContext();

  const [updatedEmail, setUpdatedEmail] = useState('');
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [updateProfileError, setUpdateProfileError] = useState<Error>();

  const switchToEditMode = useCallback(() => {
    if (canManageBilling.response) {
      setUpdatedEmail(billingProfile?.Email || '');
      setIsInEditMode(true);
    }
  }, [canManageBilling, billingProfile?.Email]);

  const closeEditMode = useCallback(() => {
    setUpdatedEmail('');
    setIsInEditMode(false);
  }, []);

  const updateEmail = useCallback(() => {
    if (canManageBilling.response) {
      const updatedProfile = { ...billingProfile, email: updatedEmail };
      setUpdateProfileError(undefined);
      updateBillingProfile(updatedProfile)
        .then(() => {
          closeEditMode();
        })
        .catch(error => {
          // Assume that any error is due to the email being invalid
          setUpdateProfileError(error);
        });
    }
  }, [
    canManageBilling,
    billingProfile,
    updateBillingProfile,
    updatedEmail,
    closeEditMode,
  ]);

  return (
    <StyledBody>
      {isInEditMode ? (
        <EditContainer>
          <EditTitleContainer>
            <StyledHeader color="SECONDARY_TEXT">Email</StyledHeader>
            <Button
              size="middle"
              shape="round"
              type="secondary"
              dataCy="discard-email-changes"
              style={CloseCircleButtonStyle}
              onClick={closeEditMode}
            >
              <Icon icon={'closeOutlined'} size={16} color="black" />
            </Button>
          </EditTitleContainer>
          {updateProfileError && (
            <StyledCardStatus
              status="error"
              text={updateProfileError.message}
            />
          )}
          <StyledInput
            onChange={setUpdatedEmail}
            placeholder="Email"
            value={updatedEmail}
            disabled={isFetchingProfile || isUpdatingProfile}
            required
            width="50%"
            error={
              isValidEmail(updatedEmail) ? '' : 'Please enter a valid email'
            }
            dataCy="email-input-field"
          />
          <Button
            size="middle"
            disabled={
              !updatedEmail || isUpdatingProfile || !isValidEmail(updatedEmail)
            }
            dataCy="save-button"
            styleOverride={SaveButtonWidth}
            onClick={updateEmail}
          >
            {isUpdatingProfile ? (
              <Spin size="small" dataCy="email-save-btn-spinner" />
            ) : (
              'Save'
            )}
          </Button>
        </EditContainer>
      ) : (
        <>
          <DetailsContainer>
            <StyledHeader color="SECONDARY_TEXT">Email</StyledHeader>
            {billingProfile?.Email ? (
              <StyledDescription>{billingProfile.Email}</StyledDescription>
            ) : (
              <StyledDescription>Not provided</StyledDescription>
            )}
          </DetailsContainer>

          <StyledButton
            minWidth={100}
            onClick={switchToEditMode}
            type="secondary"
            disabled={!canManageBilling.response || !hasSavedCard}
            dataCy="update-btn"
          >
            {isUpdatingProfile ? (
              <Spin size="small" dataCy="email-update-btn-spinner" />
            ) : (
              'Update'
            )}
          </StyledButton>
        </>
      )}
    </StyledBody>
  );
};

export default BillingEmailCard;

const StyledCardStatus = styled(CardStatus)`
  margin: 16px 0;
`;

const StyledButton = styled(Button)`
  &:disabled {
    background-color: ${Color.LIGHTER_GREY};
    color: ${Color.BORDER_GREY};
    border: none;
  }
`;

const StyledBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const EditContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledHeader = styled(Text)`
  margin-bottom: 8px;
`;

const StyledInput = styled(Input)`
  margin-bottom: 8px;
`;

const StyledDescription = styled(Text)`
  color: ${Color.PRIMARY_TEXT};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
`;

const EditTitleContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: top;
`;

const CloseCircleButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  maxWidth: 32,
  minWidth: 32,
  gap: 8,
  padding: 0,
  borderColor: Color.BORDER_GREY,
  marginLeft: 24,
};

const SaveButtonWidth = {
  width: 73,
  height: 40,
  maxWidth: 73,
  minWidth: 73,
};
